import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, useGame } from '../sdk/react';

type Prompt = { prompt: string; hint?: string };

const HELD = 'HELD';
const STOPPED = 'STOPPED';

export default function TimerHold() {
  const game = useGame();
  const { kit } = game;
  let prompts = (Array.isArray(game.config.prompts) ? game.config.prompts : [])
    .filter((x): x is Prompt => !!x && typeof (x as Prompt).prompt === 'string' && (x as Prompt).prompt.trim() !== '');
  if (prompts.length === 0 && typeof game.config.promptText === 'string' && game.config.promptText.trim()) {
    prompts = [{ prompt: game.config.promptText }];
  }
  const secs = Number(game.config.holdSeconds);
  const holdSeconds = Number.isFinite(secs) && secs > 0 ? Math.min(120, secs) : 5;
  const [current, setCurrent] = useState<Prompt | null>(null);
  const [trialId, setTrialId] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(holdSeconds);
  const [holding, setHolding] = useState(false);
  const [outcome, setOutcome] = useState<'HELD' | 'STOPPED' | null>(null);
  const [held, setHeld] = useState(0);
  const [letGo, setLetGo] = useState(0);
  const tick = useRef<number | null>(null);
  const settled = useRef<string | null>(null);

  useEffect(() => { kit.session.offerBreak(false); }, [kit]);

  function clearTick() {
    if (tick.current !== null) {
      window.clearInterval(tick.current);
      tick.current = null;
    }
  }

  useEffect(() => () => clearTick(), []);

  const settle = useCallback(async (id: string, how: 'HELD' | 'STOPPED') => {
    if (settled.current === id) return;
    settled.current = id;
    const verdict = await game.respond(id, how);
    if (!verdict) return;
    if (how === HELD) setHeld((n) => n + 1);
    else setLetGo((n) => n + 1);
    setOutcome(how);
  }, [game.respond]);

  async function start() {
    if (holding || prompts.length === 0) return;
    setOutcome(null);
    const chosen = prompts[Math.floor(Math.random() * prompts.length)];
    const id = await game.register({ prompt: chosen.prompt, holdSeconds }, { value: HELD });
    if (!id) return;
    setCurrent(chosen);
    setTrialId(id);
    setRemaining(holdSeconds);
    setHolding(true);
    game.say(chosen.prompt);
    const startedAt = kit.clock.now();
    tick.current = window.setInterval(() => {
      const left = Math.max(0, Math.ceil((holdSeconds * 1000 - kit.clock.elapsedSince(startedAt)) / 1000));
      setRemaining(left);
      if (left <= 0) {
        clearTick();
        setHolding(false);
        void settle(id, HELD);
      }
    }, 250);
  }

  function stop() {
    if (!holding || !trialId) return;
    clearTick();
    setHolding(false);
    kit.speech.stop();
    void settle(trialId, STOPPED);
    setRemaining(holdSeconds);
  }

  return (
    <GameFrame game={game} empty={prompts.length === 0} emptyText="Edit it to add what the learner should hold." onResume={() => {}} breakTrial={trialId}>
      <div className="card concept-timer">
        <div className="muted">✓ {held}{' · '}✗ {letGo}</div>
        <div className="concept-prompt-big">{current ? current.prompt : 'Ready'}</div>
        {current?.hint && !holding && <div className="muted" style={{ marginBottom: 12 }}>{current.hint}</div>}
        <div className="concept-timer-display" aria-live="polite">{holding ? remaining : holdSeconds}s</div>
        {outcome === HELD && <div className="muted">Held for the whole time.</div>}
        {outcome === STOPPED && <div className="muted">Let go early — written down as it happened.</div>}
        <div className="toolbar" style={{ justifyContent: 'center' }}>
          {!holding ? (
            <button className="btn" onClick={() => void start()} aria-label="Start the hold">{outcome ? 'Go again' : 'Start'}</button>
          ) : (
            <button className="btn secondary" onClick={stop} aria-label="Stop the hold">Stop</button>
          )}
        </div>
      </div>
    </GameFrame>
  );
}
